import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, catchError, debounceTime, map, merge, of, switchMap, takeUntil, tap } from 'rxjs';
import { RateDetailsComponent } from 'src/app/pages/agreements/components/rate-details/rate-details.component';
import { TIME_OF_DAY_DESCRIPTION } from 'src/app/pages/agreements/constants/rate-description.constants';
import { DEFAULT_RATE_SCHEDULE } from 'src/app/pages/agreements/constants/rate-schedule.constants';
import {
  PriceLimitAlertEnum,
  PriceLimitAlertToDEnum,
} from 'src/app/pages/agreements/enums/price-limit-alert-type.enum';
import { AgreementsService } from 'src/app/pages/agreements/services/agreements.service';
import {
  AgreementRatesFormGroup,
  AgreementRatesFormOutput,
  RateFormGroup,
} from 'src/app/pages/agreements/types/agreement-create-update-form.model';
import { AvailableAndDefaultBudgetSupportTypes } from 'src/app/pages/agreements/types/available-and-default-budget-support-types';
import { ProfileSearchService } from 'src/app/pages/search/profile-search.service';
import { EventTrackingEnum } from 'src/app/shared/enums/event-tracking.enum';
import { BudgetSupportType } from 'src/app/shared/graphql/agreements/get-budget-support-types-query.graphql';
import { GetMableFeesQueryResult } from 'src/app/shared/graphql/agreements/mable-fees-query.graphql';
import { GetMableFeesQueryService } from 'src/app/shared/graphql/agreements/mable-fees-query.graphql.autogenerated';
import { Agreement } from 'src/app/shared/models/gql/agreement.model';
import { RateChecker } from 'src/app/shared/models/gql/rate-checker.model';
import { Rate, RateSchedule } from 'src/app/shared/models/gql/rate.model';
import { WorkerProfile } from 'src/app/shared/models/gql/worker-profile.model';
import { NetRate, PriceLimitAlertData } from 'src/app/shared/models/price-limit-alert-data.model';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ModalDialogService } from 'src/app/shared/services/modal-dialog.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { getGraphQLErrorText } from 'src/app/shared/utilities/graphql-error-handling';
import { v4 } from 'uuid';

interface RateControl {
  formField: AbstractControl;
  formGroup: FormGroup<RateFormGroup>;
  fieldName: string;
}

/**
 * @deprecated - use AgreementServiceGroupsFormComponent for hourly rates and AgreementFlatRatesFormComponent for
 * flat rates instead.
 */

@Component({
  selector: 'app-agreement-rates-form',
  templateUrl: './agreement-rates-form.component.html',
  styleUrls: ['./agreement-rates-form.component.scss'],
})
export class AgreementRatesFormComponent implements OnInit {
  /** Agreement data */
  @Input() agreement: Agreement;

  /** Worker profile - used for indicative rates */
  @Input() workerProfile: WorkerProfile;

  /** Flag to indicate whether the user has tried to submit the rates */
  @Input() hasSubmitted = true;

  /**
   * Flagger to check whether to show info related to budget support type
   * (the rate info banner, budget support type items)
   */
  @Input() shouldShowBudgetSupportTypeInformation = false;

  /** minimum agreement rate allowed in the platform */
  @Input() minimumRate: number;

  // Available budget support types input
  @Input() set budgetSupportTypes(types: AvailableAndDefaultBudgetSupportTypes) {
    this.availableBudgetSupportTypes = types.availableBudgetSupportTypes;
    this.defaultBudgetSupportTypes = types.defaultBudgetSupportTypes;
    this.setServiceCategoriesAppliedBannerContext();
  }

  /** Emits the value and status of the rates. */
  @Output() valueChange = new EventEmitter<AgreementRatesFormOutput>();

  /** Reference to rate inputs so that the last one can be selected for focus on rate add */
  @ViewChildren('rateInput', { read: ElementRef }) rateInputs: QueryList<ElementRef>;

  /** Rates form */
  ratesForm: FormGroup<AgreementRatesFormGroup>;

  /** Array of rates that are currently being updated */
  calculatingRates: RateControl[] = [];

  // Available budget support types
  availableBudgetSupportTypes: BudgetSupportType[] = [];

  // Default budget support types
  defaultBudgetSupportTypes: BudgetSupportType[] = [];

  // specialised support types - support types that can only be added as available by Mable admin team
  specialisedBudgetSupportTypes: BudgetSupportType[] = [];

  /** Display indicative rates explanatory banner */
  shouldShowIndicativeRatesBanner = false;

  /** Data to be passed to the price limit alert component for segment event use */
  priceLimitAlertData: PriceLimitAlertData;

  /** Number of rates sections - rates where destroyed is true are not included */
  rateCount = 0;

  /**
   * Context for the Service Category Applied Banner
   */
  serviceCategoriesAppliedBannerContext: {
    shouldShowServiceCategoriesAvailableBanner: boolean;
    clientFirstName: string;
    consumerUuid: string;
    supportProviderUuid: string;
    hasSupportTypesBeenAllocatedToExistingAgreement: boolean;
    hasSpecialisedSupportItemBeenAdded: boolean;
  } = {
    shouldShowServiceCategoriesAvailableBanner: false,
    clientFirstName: '',
    consumerUuid: '',
    supportProviderUuid: '',
    hasSupportTypesBeenAllocatedToExistingAgreement: false,
    hasSpecialisedSupportItemBeenAdded: false,
  };

  // Dynamic SP Fee
  carerFeePercentage: number | string = '';

  /** The destroy subject. Used to unsubscribe the value change subscription. */
  private destroySub$ = new Subject<void>();

  constructor(
    private agreementsService: AgreementsService,
    private utilityService: UtilityService,
    private modalDialogService: ModalDialogService,
    private loaderService: LoaderService,
    private profileSearchService: ProfileSearchService,
    private getMableFeesQueryService: GetMableFeesQueryService,
  ) {}

  /**
   * Getter for rates form array
   * Used by prependRate and removeRate
   */
  get rates(): FormArray<FormGroup<RateFormGroup>> {
    return this.ratesForm.controls.rates;
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.isLoadingInProgress = true;
    await this.setWorkerProfile();
    await this.setupFormFields();

    this.setServiceCategoriesAppliedBannerContext();
    this.setupPriceLimitAlertData();
    this.setDynamicMableFees();
    this.emitValueChange();
    this.loaderService.isLoadingInProgress = false;
  }

  /**
   * On click of the edit button of a rate, open up the rate details modal.
   *
   * @param rate - form group for the rate that is being editted.
   */
  editRate(rate: FormGroup): void {
    this.modalDialogService
      .addDialogToQueue({
        component: RateDetailsComponent,
        componentProps: {
          rateCents: rate.value.rateCents,
          rateId: rate.value.id,
          agreedTempRateId: rate.value.id,
          netRate: rate.value.netRate,
          priceLimitAlertData: this.priceLimitAlertData,
          minimumHourlyRate: this.minimumRate,
          availableBudgetSupportTypes: this.availableBudgetSupportTypes,
          budgetSupportTypes: rate.value.budgetSupportTypes,
          postedJobId: this.agreement.job?.id,
          agreementGrandfatheredAt: this.agreement.grandfatheredAt,
          agreementId: this.agreement.id,
          rateName: rate.value.rateName,
          flatRate: rate.value.flatRate,
          rateDescription: rate.value.rateDescription,
          rateSchedule: rate.value.rateSchedule,
          consumerId: this.agreement.otherUser?.id,
          consumerUuid: this.agreement.otherUser?.uuid,
          supportProviderId: this.workerProfile?.id,
          supportProviderUuid: this.workerProfile?.user?.uuid,
          workerType: this.workerProfile?.myType,
        },
        cssClass: 'fullpage-modal',
      })
      .subscribe((response) => {
        if (response?.data) {
          rate.patchValue({
            rateName: response.data.rateName,
            flatRate: response.data.flatRate,
            rateDescription: response.data.rateDescription,
            rateSchedule: response.data.rateSchedule,
            rateCents: response.data.rateCents,
            rateDay: this.agreementsService.getDayOfWeekDescription(response.data.rateSchedule),
            rateTime: this.agreementsService.getTimeOfDayDescription(response.data.rateSchedule),
            budgetSupportTypes: response.data.budgetSupportTypes,
          });
          if (response.data.displaySegment) {
            this.rateUpdateSegmentCall(rate.value);
          }
          this.emitValueChange();
        }
      });
  }

  /**
   * Add a new rate to the form.
   */
  addRate(): void {
    this.modalDialogService
      .addDialogToQueue({
        component: RateDetailsComponent,
        componentProps: {
          priceLimitAlertData: this.priceLimitAlertData,
          minimumHourlyRate: this.minimumRate,
          availableBudgetSupportTypes: this.availableBudgetSupportTypes,
          postedJobId: this.agreement.job?.id || null,
          agreementGrandfatheredAt: this.agreement.grandfatheredAt || null,
          agreementId: this.agreement.id,
          consumerId: this.agreement.otherUser?.id,
          consumerUuid: this.agreement.otherUser?.uuid,
          supportProviderId: this.workerProfile?.id,
          supportProviderUuid: this.workerProfile?.user?.uuid,
          agreedTempRateId: v4(),
          rateId: null,
          budgetSupportTypes: this.defaultBudgetSupportTypes,
          isNewRate: true,
          workerType: this.workerProfile?.myType,
        },
        cssClass: 'fullpage-modal',
      })
      .subscribe(async (response) => {
        if (response?.data) {
          const rateDetails = {
            id: null,
            rateName: response.data.rateName,
            flatRate: response.data.flatRate,
            rateDescription: response.data.rateDescription,
            rateSchedule: response.data.rateSchedule,
            rateCents: response.data.rateCents * 100,
            destroy: false,
            tempRateId: response.data?.tempRateId,
            budgetSupportTypes: response.data.budgetSupportTypes,
            showCapRateBanner: response.data.showCapRateBanner,
          };
          this.rates.push(await this.ratesToFormGroup(rateDetails));

          // reset the rate listeners to include the newly added rate
          this.rateChangeListener();

          // focus on the newly added rate value input
          this.focusLastRateInput();

          const selectedRate = this.rates.controls[this.rates.length - 1];
          this.checkPriceLimitAlert(selectedRate);
          if (response.data.displaySegment) {
            // Using Rate cast because formGroup makes the value Partial<>
            this.rateUpdateSegmentCall(selectedRate.value as Rate);
          }
          this.emitValueChange();
        }
      });
  }

  /**
   * Remove rate at position `rateIndex` from the form if its a new rate.
   * If existing rate change the 'destroy' attribute to true.
   *
   * @param rateIndex - The index for the rate to be removed.
   */
  removeRate(rateIndex: number): void {
    if (this.rates.controls[rateIndex].controls.id.value != null) {
      this.rates.controls[rateIndex].controls.destroy.setValue(true);
    } else {
      this.rates.removeAt(rateIndex);
    }

    this.rateCount -= 1;
    if (this.rateCount === 0) this.shouldShowIndicativeRatesBanner = false;
    this.emitValueChange();
  }

  /**
   * Set the worker profile object.
   */
  private async setWorkerProfile(): Promise<void> {
    // No need to set workerProfile if it's already being set from the parent component.

    try {
      this.workerProfile = await this.profileSearchService.getWorkerProfileProperties(true);
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Setting up the initial form values.
   */
  private async setupFormFields(): Promise<void> {
    try {
      this.ratesForm = new FormGroup<AgreementRatesFormGroup>({
        rates: new FormArray(await this.setRates()),
      });

      this.rateChangeListener();
      this.checkPriceLimitOnLoad();
    } catch (error) {
      this.utilityService.showErrorAlert('Error retrieving and setting information, please retry later.');
    }
  }

  /**
   * Create the rates for the form array based on either the agreement rates (if they exist) or the
   * worker profile's indicative rates.
   *
   * @returns an array of rate form groups.
   */
  private async setRates(): Promise<FormGroup<RateFormGroup>[]> {
    try {
      // if existing agreement, pre-populate with agreement rates
      if (this.agreement.id && this.agreement.status !== 'requested') {
        const agreementRates = this.agreement.agreementRates.map(
          async (rate: Rate) => await this.ratesToFormGroup(rate),
        );
        return Promise.all(agreementRates);
      }

      // otherwise pre-populate with default rates specified in carer's profile and show the indicative rates banner
      if (this.workerProfile?.rates?.length) {
        this.shouldShowIndicativeRatesBanner = true;

        const indicativeRates = this.workerProfile.rates
          .map((rate) => ({
            id: null,
            rateName: rate.rateName,
            // make the carer profile rates compatible with agreement rates by converting dollars to cents.
            rateCents: parseFloat(rate.dollars.replace('$', '')) * 100,
            flatRate: rate.flatRate,
            rateDescription: rate.rateDescription,
            rateSchedule: rate.rateSchedule,
          }))
          .map(async (rate: Rate) => await this.ratesToFormGroup(rate));
        return Promise.all(indicativeRates);
      }
      return [];
    } catch (error) {
      console.error('Cannot set rates in agreement rates form', error);
      return []; // allows the code to continue without error
    }
  }

  /**
   * Convert rate to form group.
   *
   * @param rate - Rate.
   */
  private async ratesToFormGroup(rate: Rate): Promise<FormGroup<RateFormGroup>> {
    try {
      this.rateCount += 1;

      // setting up temporary rate id - used for segments
      let tempRateId;
      if (rate.tempRateId) {
        tempRateId = rate.tempRateId;
      } else if (!rate.id) {
        tempRateId = v4();
      } else {
        tempRateId = '';
      }

      const budgetSupportTypes = rate.budgetSupportTypes?.length
        ? rate.budgetSupportTypes
        : this.defaultBudgetSupportTypes;

      // set the budgetSupportTypes to the default budgetSupportTypes on creation if it exists
      // so that calculatedRates API can be called with budgetSupportTypes
      rate.budgetSupportTypes = budgetSupportTypes;

      // get the calculated net rate (i.e. including mable fees) from backend
      const calculatedRateRequestObject = this.getCalculatedRateRequestObject(rate, true);
      const netRate: NetRate = await this.agreementsService.getRates(calculatedRateRequestObject);

      const newFormGroup = new FormGroup<RateFormGroup>({
        id: new FormControl<string>(rate.id),
        rateName: new FormControl<string>(rate.rateName, [Validators.required]),
        rateDescription: new FormControl<string>(rate.rateDescription),
        rateCents: new FormControl<number>(rate.rateCents / 100.0, [
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
          Validators.min(rate?.flatRate ? 1 : this.minimumRate),
          Validators.max(9999.99),
        ]),
        flatRate: new FormControl<boolean>(rate.flatRate),
        destroy: new FormControl<boolean>(false),
        rateWithFee: new FormControl<string>(netRate.rateWithFee),
        exceedCapRate: new FormControl<boolean>(netRate.exceedCapRate),
        rateSchedule: new FormControl<RateSchedule>(rate.rateSchedule),
        showCapRateBanner: new FormControl<boolean>(false),
        budgetSupportTypes: new FormControl<BudgetSupportType[]>(budgetSupportTypes),
        priceLimitAlertType: new FormControl<string>(
          this.getPriceLimitAlertType(netRate.priceLimitAlertType, netRate.priceLimitAlertToDType, budgetSupportTypes),
        ),
        dayOfWeekSelected: new FormControl<string>(netRate.priceLimitAlertType),
        timeOfDaySelected: new FormControl<string>(netRate.priceLimitAlertToDType),
        capRate: new FormControl<number>(netRate.capRate),
        rateDay: new FormControl<string>(this.agreementsService.getDayOfWeekDescription(rate.rateSchedule)),
        rateTime: new FormControl<string>(this.agreementsService.getTimeOfDayDescription(rate.rateSchedule)),
        tempRateId: new FormControl<string>(tempRateId),
        supportLocation: new FormControl<string>(netRate.supportLocation),
        supportType: new FormControl<string>(netRate.supportType),
        supportName: new FormControl<string>(netRate.supportName),
      });

      // show any possible errors for the initially populated rate
      newFormGroup.controls.rateCents.markAsDirty();
      newFormGroup.controls.rateCents.updateValueAndValidity();

      return newFormGroup;
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
      return null; // allows the code to continue without error
    }
  }

  /**
   * Calls the calculatedRate API to check whether the value and schedule of a rate combined exceeds
   * a cap rate limit set by the NDIS.
   *
   * @param rateFormGroup - The rate in form group that will be updated with values from the API response.
   */
  private async getCalculatedRate(rateFormGroup: FormGroup<RateFormGroup>): Promise<void> {
    // Avoid api call in case of invalid field data
    if (
      !rateFormGroup.get('rateWithFee').valid ||
      !rateFormGroup?.get('rateCents')?.value ||
      rateFormGroup.get('rateCents').errors?.pattern ||
      isNaN(rateFormGroup.get('rateCents').value)
    ) {
      rateFormGroup.patchValue({
        exceedCapRate: false,
      });
      return;
    }

    const calculatedRateRequestObject = this.getCalculatedRateRequestObject(rateFormGroup.value as Rate);
    try {
      const netRate: NetRate = await this.agreementsService.getRates(calculatedRateRequestObject);
      // check if net rate is exists
      if (netRate) {
        // get rate controls and update the rate from api response
        rateFormGroup.patchValue({
          rateWithFee: netRate.rateWithFee,
          exceedCapRate: netRate.exceedCapRate,
          priceLimitAlertType: this.getPriceLimitAlertType(
            netRate.priceLimitAlertType,
            netRate.priceLimitAlertToDType,
            rateFormGroup.value.budgetSupportTypes,
          ),
          dayOfWeekSelected: netRate.priceLimitAlertType,
          timeOfDaySelected: netRate.priceLimitAlertToDType,
          capRate: netRate.capRate,
          supportLocation: netRate.supportLocation,
          supportType: netRate.supportType,
          supportName: netRate.supportName,
        });
      }
    } catch (error) {
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Given a rate object, return the request object for the calculatedRate API to get the net rate.
   *
   * @param rate - the rate object containing the gross fees.
   * @param indicativeRate - the rate comes from profile rates (and therefore are in cents instead of dollars)
   * @returns the request object for calculatedRate API call.
   */
  private getCalculatedRateRequestObject(rate: Rate, indicativeRate = false): RateChecker {
    const timeAndPlace = {
      rateSchedule: rate.rateSchedule || DEFAULT_RATE_SCHEDULE,
    };

    let requestObject: RateChecker = {
      agreementId: this.agreement?.id,
      baseRate: indicativeRate ? rate.rateCents / 100 : +rate.rateCents,
      consumerId: this.agreement.otherUser.id,
      grandfatheredAt: this.agreement.grandfatheredAt,
      timeAndPlace,
    };

    const includeBudgetSupportTypes = this.willIncludeBudgetSupportTypes(rate.budgetSupportTypes);
    if (includeBudgetSupportTypes) {
      const budgetSupportTypesIds: string[] = rate.budgetSupportTypes.map((supportType) => supportType.id);

      requestObject = {
        ...requestObject,
        budgetSupportTypes: budgetSupportTypesIds,
        postedJobId: this.agreement.job?.id || null,
      };
    }

    return requestObject;
  }

  /**
   * Retrieves Mable fees from the server.
   */
  private fetchDynamicMableFees(): Observable<GetMableFeesQueryResult> {
    return this.getMableFeesQueryService.fetch({ agreementId: String(this.agreement.id) }).pipe(
      map((response) => response.data.mableFees),
      catchError((error) => {
        console.error('[ViewAgreementPage] Error retrieving Mable fees', error);
        return of(null);
      }),
      takeUntil(this.destroySub$),
    );
  }

  /**
   * Sets the dynamic Mable fees from the server.
   */
  private setDynamicMableFees(): void {
    this.fetchDynamicMableFees()
      .pipe(takeUntil(this.destroySub$))
      .subscribe((mableFees: GetMableFeesQueryResult) => {
        this.carerFeePercentage = mableFees?.workerFeePercentage;
      });
  }

  /**
   * Returns a string representing what should be displayed on the price limit alert.
   *
   * @param dayOfWeek - priceLimitAlertType passed from the backend indicating day of the week.
   * @param timeOfDay - priceLimitAlertTodType passed from the backend indicating time of the day.
   * @param budgetSupportTypes - budgetSupportTypes on the rate.
   * @returns - String representation of what the price limit alert type refers to.
   */
  private getPriceLimitAlertType(
    dayOfWeek: PriceLimitAlertEnum,
    timeOfDay: PriceLimitAlertToDEnum,
    budgetSupportTypes: BudgetSupportType[],
  ): string {
    const showBudgetSupportTypes = this.shouldShowBudgetSupportTypeInformation && budgetSupportTypes?.length > 0;
    if (timeOfDay) {
      return `${dayOfWeek}, ${TIME_OF_DAY_DESCRIPTION[timeOfDay.toLocaleLowerCase()]}${
        showBudgetSupportTypes ? '' : '.'
      }`;
    }

    return `${dayOfWeek}${showBudgetSupportTypes ? '' : '.'}` || 'Weekday between 6am - 8pm';
  }

  /**
   * Listen to changes to the rateCents, flatRate and rateSchedule controls of each rate form group.
   * If changes are to rateCents and rateSchedule, then get check the calculatedRate API to see if the rate is
   * exceeding the rate cap; flatRate change does not need to call the API as it does not get taken into consideration
   * in the backend API call. Flat rates currently do not trigger any price limit alerts.
   *
   * Check and display any price limit alerts if exceedCapRate is true and flatRate is false.
   */
  private rateChangeListener(): void {
    // Unsubscribe existing subscriptions first
    this.destroySub$.next();

    const getCalculatedRateStream = async (rateControl: RateControl): Promise<RateControl> => {
      const { formGroup, fieldName } = rateControl;
      if (formGroup) {
        if (fieldName === 'rateCents' || fieldName === 'rateSchedule') {
          await this.getCalculatedRate(formGroup);
        }
        return rateControl;
      }
    };

    // create an array of form fields to listen to
    const controlsToListen: RateControl[] = [];
    const ratesFormArray = this.ratesForm.controls.rates;
    ratesFormArray.controls.forEach((rateControl: FormGroup<RateFormGroup>) => {
      controlsToListen.push({
        formField: rateControl.get('rateCents'),
        formGroup: rateControl,
        fieldName: 'rateCents',
      });
      controlsToListen.push({ formField: rateControl.get('flatRate'), formGroup: rateControl, fieldName: 'flatRate' });
      controlsToListen.push({
        formField: rateControl.get('rateSchedule'),
        formGroup: rateControl,
        fieldName: 'rateSchedule',
      });
    });

    // subscribe to value change of the 3 fields for each rate and recalculate net rate and check price limits
    merge(
      ...controlsToListen.map((control) =>
        control.formField.valueChanges.pipe(
          tap(() => {
            // if the current rate formGroup has not been added to the calculatingRates array, add it to the array so
            // that propagation of the parent form is prevented until the calculations are complete
            if (this.calculatingRates.indexOf(control) < 0) {
              this.calculatingRates.push(control);
            }
            this.emitValueChange();
          }),
          debounceTime(200),
          switchMap(() => getCalculatedRateStream(control)),
        ),
      ),
    )
      .pipe(takeUntil(this.destroySub$))
      .subscribe((rateControl) => {
        if (rateControl?.formGroup) {
          this.checkPriceLimitAlert(rateControl.formGroup);
        }

        // remove the formGroup from the calculatingRates array so that propagation of the parent form can
        // continue of the array is empty
        const formGroupIndex = this.calculatingRates.indexOf(rateControl);
        if (formGroupIndex >= 0) this.calculatingRates.splice(formGroupIndex, 1);
        this.emitValueChange();
      });
  }

  /**
   * Emits the value and status of the rates. Status is based on the following conditions:
   * 1. There is at least one rate.
   * 2. The existing rates are valid (i.e. has a rate and name).
   * 3. There are no current calculatedRate API calls that have not yet resolved.
   */
  private emitValueChange(): void {
    this.valueChange.emit({
      rates: this.ratesForm.controls.rates.value,
      status: this.ratesForm.valid && this.rateCount > 0 && this.calculatingRates.length === 0,
    });
  }

  /**
   * Determine if the price limit alert banner should show or not.
   * The alert banner should only show for non flat-rates that have exceeded the limit.
   *
   * @param rate - The rate form group to be checked.
   */
  private checkPriceLimitAlert(rate: FormGroup<RateFormGroup>): void {
    if (!rate.controls.flatRate.value && rate.controls.exceedCapRate.value) {
      rate.controls.showCapRateBanner.setValue(true);
    } else {
      rate.controls.showCapRateBanner.setValue(false);
    }
  }

  /**
   * Check whether the service category applied banner should so and if so, setup all the relevent properties.
   */
  private async setServiceCategoriesAppliedBannerContext(): Promise<void> {
    try {
      this.serviceCategoriesAppliedBannerContext.shouldShowServiceCategoriesAvailableBanner =
        await this.checkShowServiceCategoriesAvailableBanner();
      if (this.serviceCategoriesAppliedBannerContext.shouldShowServiceCategoriesAvailableBanner) {
        this.serviceCategoriesAppliedBannerContext = {
          ...this.serviceCategoriesAppliedBannerContext,
          clientFirstName: this.agreement.otherUser.firstName,
          consumerUuid: this.agreement.otherUser?.uuid,
          supportProviderUuid: this.workerProfile?.user?.uuid,
        };
      }
    } catch (error) {
      console.error('Error fetching default budget types in agreement rates');
      this.utilityService.showErrorAlert(getGraphQLErrorText(error));
    }
  }

  /**
   * Setup the price limit alert data for segment tracking.
   */
  private setupPriceLimitAlertData(): void {
    this.priceLimitAlertData = {
      agreementType: 'Standard',
      agreementId: this.agreement.uuid,
      clientId: this.agreement.otherUser?.uuid,
      legacyAgreement: this.agreement.grandfatheredAt !== null && this.agreement.grandfatheredAt !== undefined,
      workerType: Array.isArray(this.workerProfile?.myType)
        ? this.workerProfile.myType.toString()
        : this.workerProfile.myType,
    };
  }

  /**
   * Checks whether the Show Service Categories Available Banner should be displayed or not.
   * This banner will be visible in case the conditions bellow are fulfilled:
   *
   * 1) Client is a budget user
   * 2) Either:
   *  2.1) No support types have been allocated to existing agreement yet
   *  2.2) Specialised support type has been added as available by Mable finance team but not selected as default by the SP for any rates yet
   *
   * @returns true if the banner should be visible, false otherwise.
   */
  private async checkShowServiceCategoriesAvailableBanner(): Promise<boolean> {
    // 1) Checks whether the client is eligible for service categories
    if (!this.shouldShowBudgetSupportTypeInformation) return false;

    // 2) One of the 2 following options:
    // 2.1) Support types not allocated to existing agreement yet
    this.serviceCategoriesAppliedBannerContext.hasSupportTypesBeenAllocatedToExistingAgreement =
      this.checkIfSupportTypesHaveBeenAllocatedToAnyRate();
    // 2.2) Speciailised support type added as available but not selected as default by the SP for any rates yet
    this.serviceCategoriesAppliedBannerContext.hasSpecialisedSupportItemBeenAdded =
      this.checkIfAnySpecialisedSupportItemBeenAdded();

    return (
      !this.serviceCategoriesAppliedBannerContext.hasSupportTypesBeenAllocatedToExistingAgreement ||
      this.serviceCategoriesAppliedBannerContext.hasSpecialisedSupportItemBeenAdded
    );
  }

  /**
   * It checks whether any speciailised support type added as available but not selected as default by the SP for any rates yet.
   *
   * @returns true if any speciailised support type added as available but not selected as default by the SP for any rates yet, false otherwise.
   */
  private checkIfAnySpecialisedSupportItemBeenAdded(): boolean {
    // check if there is any speciailised support type added as available
    this.specialisedBudgetSupportTypes = this.availableBudgetSupportTypes.filter(
      (supportType) => supportType.isAdminOnlyType,
    );

    // there is no speciailised support type added as available, so no need to check if it has been selected for any rate
    if (!(this.specialisedBudgetSupportTypes && this.specialisedBudgetSupportTypes.length > 0)) return false;

    const allBudgetSupportTypeFromAllRates: BudgetSupportType[] = [];
    this.agreement.agreementRates?.forEach((rate) => {
      if (rate.budgetSupportTypes && rate.budgetSupportTypes.length)
        allBudgetSupportTypeFromAllRates.push(...rate.budgetSupportTypes);
    });

    const listOfSpecialisedSupportTypeThatHaveBeenAddedToRates = this.specialisedBudgetSupportTypes.filter(
      (specialisedSupportType) =>
        allBudgetSupportTypeFromAllRates.some((supportType) => supportType.id === specialisedSupportType.id),
    );
    return this.specialisedBudgetSupportTypes.length !== listOfSpecialisedSupportTypeThatHaveBeenAddedToRates?.length;
  }

  /**
   * It checks whether any support types have been allocated to any agreement rate.
   *
   * @returns true if any support types have been allocated to any agreement rate, false otherwise.
   */
  private checkIfSupportTypesHaveBeenAllocatedToAnyRate(): boolean {
    const hasSupportTypesBeenAllocatedToAgreement = this.agreement.agreementRates?.some(
      (rate) => rate.budgetSupportTypes && rate.budgetSupportTypes.length,
    );
    return !!hasSupportTypesBeenAllocatedToAgreement;
  }

  /**
   * Checks whether the budget support types array should be included in the request object
   * needed to get calculated rates from back-end.
   *
   * @param budgetSupportTypes - budget support types array
   * @returns true if budgetSupportTypes should be included in the request object of getRate, false otherwise.
   */
  private willIncludeBudgetSupportTypes(budgetSupportTypes: BudgetSupportType[]): boolean {
    return this.shouldShowBudgetSupportTypeInformation && budgetSupportTypes && budgetSupportTypes.length > 0;
  }

  /**
   * Focuses on the last rate input.
   */
  private focusLastRateInput(): void {
    // add a delay before focusing to wait for input to render
    setTimeout(() => {
      this.rateInputs?.last?.nativeElement?.setFocus();
    }, 500);
  }

  /**
   * On screen load check price limit condition for each form group in rates form array
   */
  private checkPriceLimitOnLoad(): void {
    this.rates.controls.map((control) => this.checkPriceLimitAlert(control));
  }

  /**
   * Call segment event on rate details updated.
   *
   * @param rate - Rate form values
   */
  private rateUpdateSegmentCall(rate: Rate): void {
    const properties = {
      ...this.priceLimitAlertData,
      rate,
    };
    this.agreementsService.agreementSegmentCall(EventTrackingEnum.SP_RATE_DETAILS_UPDATED, properties, false);
  }
}
